import { Box } from '@mui/material';
import UPInput from 'components/input';
import { memo, useEffect, useRef, useState } from 'react';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/DeleteGrey.svg';

interface DeleteActionCellProps {
  id: number;
  disabled: boolean;
  onDelete: (id: number) => void;
}

export const DeleteActionCell = memo(({ id, disabled, onDelete }: DeleteActionCellProps) => (
  <Box
    onClick={() => !disabled && onDelete(id)}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.4 : 1,
    }}
  >
    <DeleteIcon />
  </Box>
));

interface WageInputCellProps {
  id: number;
  value: number;
  disabled: boolean;
  error: boolean;
  onChange: (id: number, value: number) => void;
}

// 0 is treated as "unset" (a valid wage exemption must be > 0), so it renders as an empty field.
// This only affects the initial/reset display — mid-typing values are held in local state below.
const numberToText = (value: number) =>
  value === null || value === undefined || Number.isNaN(value) || value === 0 ? '' : String(value);

// Controlled decimal input backed by local string state. A plain controlled type="number" input
// round-trips through Number() on every keystroke, which wipes intermediate values (e.g. a trailing
// "." reports an empty value in Chrome) and makes decimals impossible to type. Keeping the raw text
// locally lets the user type freely while we still push a parsed number up to the grid state.
export const WageInputCell = memo(({ id, value, disabled, error, onChange }: WageInputCellProps) => {
  const [text, setText] = useState<string>(() => numberToText(value));
  const lastPushed = useRef<number>(value);

  useEffect(() => {
    // Resync only when the value changes externally (e.g. a reset/reload), never for our own echo.
    if (value !== lastPushed.current) {
      setText(numberToText(value));
      lastPushed.current = value;
    }
  }, [value]);

  const handleChange = (event: { target: { value: string } }) => {
    const raw = event.target.value;
    if (!/^\d*\.?\d*$/.test(raw)) {
      return;
    }
    setText(raw);
    const parsed = raw === '' || raw === '.' ? 0 : Number(raw);
    const next = Number.isNaN(parsed) ? 0 : parsed;
    lastPushed.current = next;
    onChange(id, next);
  };

  return (
    <UPInput
      label=""
      placeholder=""
      type="text"
      value={text}
      disabled={disabled}
      error={error}
      onChange={handleChange}
    />
  );
});
